import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner } from "../../../../constants/const";
import { encryptStorage } from "../../../../constants/EncryptStorage";
import { GetLanguageString } from "../../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../../helper/Method";
import { ApiFinance } from "../../ApiFinance";
import { AmanatAccountBalanceCard } from "../../BalanceCards";
import { getMinMaxDates, setGlobalPastMonthLockDay, validateTransactionDate } from "../../fiscalYearHelper";
import SalaryEntryCard from "./SalaryEntryCard";
import { entryChipColor, entryTitle, formatRs, readErrorMessage } from "./salaryTransferUtils";

const toApiDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Manage -> Funds -> Expenses -> Salary from Amanat Account (Halqa / Maqami Tanzeem).
 * Lists the unit's salaries (FA quick entries) by employee; the user can only write memos or
 * remove whole salaries, then submits them for Ameer -> Markaz approval.
 */
const SalaryTransferFromAmanat = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));

  const [form, setForm] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [date, setDate] = useState(new Date());
  const [memo, setMemo] = useState("");
  const [removed, setRemoved] = useState({});
  const [memos, setMemos] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    ApiFinance()
      .GetPastMonthLockStatus()
      .then((res) => {
        if (res?.data?.lockDay !== undefined) setGlobalPastMonthLockDay(res.data.lockDay);
      })
      .catch(() => {});

    ApiFinance()
      .GetCashToAmanatTransferData(loggedInUnitId)
      .then((res) => setBalanceData(res.data))
      .catch((err) => console.error("Failed to fetch balance data", err));

    ApiFinance()
      .GetSalaryTransferForm(loggedInUnitId, toApiDate(new Date()))
      .then((res) => setForm(res.data))
      .catch((err) => setLoadError(readErrorMessage(err, t("finance_salary_load_error"))))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entries = useMemo(() => form?.entries || [], [form]);
  const selected = useMemo(
    () => entries.filter((e) => e.isSubmittable && !removed[e.quickEntryId]),
    [entries, removed]
  );
  const total = useMemo(() => selected.reduce((sum, e) => sum + (Number(e.amount) || 0), 0), [selected]);
  const effectiveBalance = Number(form?.amanatAccount?.effectiveBalance) || 0;
  const exceedsBalance = total > effectiveBalance;

  const validate = () => {
    const next = {};
    const dateError = validateTransactionDate(date);
    if (!date) next.date = t("finance_salary_date_required");
    else if (dateError) next.date = dateError;
    if (!memo.trim()) next.memo = t("finance_salary_memo_required");
    if (selected.length === 0) next.entries = t("finance_salary_select_one");
    else if (exceedsBalance) next.entries = t("finance_salary_exceeds_balance");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      unitId: loggedInUnitId,
      date: toApiDate(date),
      memo: memo.trim(),
      entries: selected.map((entry) => ({
        quickEntryId: entry.quickEntryId,
        lines: (entry.lines || []).map((line) => ({
          lineId: line.lineId,
          lineMemo: (memos[line.lineId] || "").trim(),
        })),
      })),
    };

    setSubmitting(true);
    ApiFinance()
      .AddSalaryTransferFromAmanat(payload)
      .then((res) => {
        swal({
          title: "Success",
          text: t(res?.data?.message || "finance_salary_submitted_successfully"),
          icon: "success",
          buttons: "OK",
          className: GetSelectedLanguage() === "en" ? "en" : "ur",
        }).then(() => history.goBack());
      })
      .catch((err) => {
        swal({
          title: "Error",
          text: String(t(readErrorMessage(err, "finance_salary_submit_error"))),
          icon: "error",
          buttons: "OK",
        });
        setSubmitting(false);
      });
  };

  const { minDate, maxDate } = getMinMaxDates(date);

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>
            <GetLanguageString props="finance_salary_from_amanat" />
          </h5>
          <Button className={GetCustomStyles().btn_style} color="white" size="xs" onClick={() => history.goBack()}>
            <IoArrowBackSharp color="#18A689" size={15} style={{ marginRight: 5, marginBottom: 1 }} />
            <GetLanguageString props="courses_participants_back" />
          </Button>
        </div>

        <div className="ibox-content">
          {loading || submitting ? (
            LoadingSpinner()
          ) : loadError ? (
            <div className="bg-red-50" style={{ padding: "14px 16px", color: "#991b1b" }}>
              {t(loadError)}
            </div>
          ) : (
            <>
              <Row>
                <AmanatAccountBalanceCard
                  balance={balanceData?.amanatAccountBalance}
                  effectiveBalance={
                    balanceData?.amanat_effective_balance ??
                    balanceData?.amanatEffectiveBalance ??
                    balanceData?.amanatAccountBalance
                  }
                  ledgerBalance={
                    balanceData?.amanat_ledger_balance ??
                    balanceData?.amanatLedgerBalance ??
                    balanceData?.amanatAccountBalance
                  }
                  pendingHolds={balanceData?.amanat_pending_holds ?? balanceData?.amanatPendingHolds ?? 0}
                  loading={!balanceData}
                  colProps={{ xs: "12", md: "12", className: "mb-3" }}
                />
              </Row>

              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" /> *
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={date}
                      minDate={minDate}
                      maxDate={maxDate}
                      onChange={(value) => {
                        setDate(value ?? new Date());
                        setErrors((prev) => ({ ...prev, date: "" }));
                      }}
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && <h4 className="text-error">{errors.date}</h4>}
                  </FormGroup>
                </Col>
                <Col md="8">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="transactions_memo" /> *
                    </Label>
                    <Input
                      type="text"
                      value={memo}
                      maxLength={250}
                      onChange={(e) => {
                        setMemo(e.target.value);
                        setErrors((prev) => ({ ...prev, memo: "" }));
                      }}
                    />
                    {errors.memo && <h4 className="text-error">{errors.memo}</h4>}
                  </FormGroup>
                </Col>
              </Row>

              <div className="ibox m-t-sm" style={{ marginBottom: 0 }}>
                <div className="ibox-title">
                  <h5>
                    <GetLanguageString props="finance_salary_salaries" />{" "}
                    <span style={{ color: "#6b7280" }}>({entries.length})</span>
                  </h5>
                </div>
                <div className="ibox-content">
                  <p style={{ color: "#6b7280", marginBottom: "14px" }}>
                    <GetLanguageString props="finance_salary_fixed_amounts_hint" />
                  </p>

                  {form?.pendingEntryCount > 0 && (
                    <div
                      style={{
                        background: "#FFFDF5",
                        border: "1px solid #FDE68A",
                        borderLeft: "4px solid #F59E0B",
                        borderRadius: "10px",
                        padding: "10px 14px",
                        color: "#92400E",
                        marginBottom: "14px",
                      }}
                    >
                      {t("finance_salary_pending_hidden", { count: form.pendingEntryCount })}
                    </div>
                  )}

                  {entries.length === 0 ? (
                    <div className="text-center" style={{ padding: "30px", color: "#6b7280" }}>
                      <GetLanguageString props="finance_salary_no_entries" />
                    </div>
                  ) : (
                    entries.map((entry, index) =>
                      entry.isSubmittable ? (
                        <SalaryEntryCard
                          key={entry.quickEntryId}
                          entry={entry}
                          color={entryChipColor(index)}
                          removed={!!removed[entry.quickEntryId]}
                          memos={memos}
                          onMemoChange={(lineId, value) => setMemos((prev) => ({ ...prev, [lineId]: value }))}
                          onToggleRemove={() =>
                            setRemoved((prev) => ({ ...prev, [entry.quickEntryId]: !prev[entry.quickEntryId] }))
                          }
                          disabled={submitting}
                        />
                      ) : (
                        <div
                          key={entry.quickEntryId}
                          className="bg-red-50"
                          style={{ padding: "12px 16px", marginBottom: "14px", color: "#7f1d1d" }}
                        >
                          <strong>{entryTitle(entry)}</strong> — <GetLanguageString props="finance_salary_not_submittable" />
                        </div>
                      )
                    )
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                  borderTop: "1px solid #e7eaec",
                  paddingTop: "16px",
                  marginTop: "16px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ color: "#6b7280", marginRight: "8px" }}>
                    <GetLanguageString props="finance_salary_total" />
                  </span>
                  <span style={{ fontSize: "18px", fontWeight: 800, color: exceedsBalance ? "#b91c1c" : "#047857" }}>
                    {formatRs(total)}
                  </span>
                  {errors.entries && <h4 className="text-error" style={{ marginBottom: 0 }}>{errors.entries}</h4>}
                </div>
                <Button color="white" onClick={() => history.goBack()} disabled={submitting}>
                  <GetLanguageString props="common_cancel" />
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={submitting || selected.length === 0}>
                  <GetLanguageString props="finance_salary_submit_for_approval" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(SalaryTransferFromAmanat);
